/**
 * IntegrationLogo — single render path for any integration mark in
 * the app. Resolution order:
 *
 *   1. Simple Icons brand path (full color from the brand override
 *      table, falls back to Simple Icons' own hex).
 *   2. Colored monogram (MUI Avatar w/ brand color background, first
 *      letter of the integration name as the glyph). Used for brands
 *      Simple Icons no longer ships (Slack, Salesforce, LinkedIn,
 *      DocuSign, Microsoft suite, Monday).
 *   3. Neutral grey monogram. Reserved for the "we have no idea what
 *      this integration is" case (a manifest entry the frontend has
 *      never seen). Caught by the coverage test in
 *      `IntegrationLogo.test.tsx` so it should never ship.
 *
 * Variants:
 *   - "color" (default): brand-colored fill on the SVG path; brand-
 *     colored background + white glyph on the monogram. Use for
 *     hero placements (catalog grid, JIT step pane).
 *   - "mono": currentColor on the SVG path; transparent monogram with
 *     a brand-colored ring. Use in dense rows where a full-color
 *     splash would clash (audit table, list cards).
 */

import React from 'react';
import {Avatar, Box, type SxProps, type Theme} from '@mui/material';
import {resolveIntegrationBranding} from './integrationBranding';

interface Props {
  /** Manifest id, e.g. "hubspot" or "google-drive". */
  integrationId: string;
  /** Display name; the first character is the monogram glyph. */
  name?: string;
  /** Pixel edge length. Defaults to 24. */
  size?: number;
  /** Visual treatment — see file header. Defaults to "color". */
  variant?: 'color' | 'mono';
  /** Optional override for accessibility — defaults to `name`. */
  ariaLabel?: string;
  /** Forwarded to the outer wrapper (Avatar or SVG). */
  sx?: SxProps<Theme>;
}

function readableTextOn(hex: string): string {
  // YIQ contrast formula — cheap and correct for the white/black
  // fork on monogram glyphs against a brand background. Avoids
  // pulling in a color library just for one decision.
  const n = parseInt(hex, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128 ? '#000' : '#fff';
}

export const IntegrationLogo: React.FC<Props> = ({
  integrationId,
  name,
  size = 24,
  variant = 'color',
  ariaLabel,
  sx,
}) => {
  const branding = resolveIntegrationBranding(integrationId);
  const label = ariaLabel ?? name ?? integrationId;
  const glyph = (name ?? integrationId).charAt(0).toUpperCase() || '?';

  if (branding?.path) {
    const fillColor =
      variant === 'mono' ? 'currentColor' : `#${branding.hex}`;
    return (
      <Box
        component="svg"
        role="img"
        aria-label={label}
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          width: size,
          height: size,
          fill: fillColor,
          flexShrink: 0,
          display: 'inline-block',
          ...sx,
        }}
      >
        <title>{label}</title>
        <path d={branding.path} />
      </Box>
    );
  }

  // No SVG path — fall back to a colored monogram. When `variant`
  // is "mono" we render an outlined ring instead of a filled disc to
  // stay quiet in dense rows.
  if (branding) {
    const bg = variant === 'mono' ? 'transparent' : `#${branding.hex}`;
    const fg = variant === 'mono' ? `#${branding.hex}` : readableTextOn(branding.hex);
    return (
      <Avatar
        aria-label={label}
        sx={{
          width: size,
          height: size,
          bgcolor: bg,
          color: fg,
          fontSize: size * 0.5,
          fontWeight: 600,
          flexShrink: 0,
          border: variant === 'mono' ? `1.5px solid #${branding.hex}` : 'none',
          ...sx,
        }}
      >
        {glyph}
      </Avatar>
    );
  }

  // Unknown integration. Stay quiet rather than throwing — the
  // coverage test exists to keep this path empty in production.
  return (
    <Avatar
      aria-label={label}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.5,
        fontWeight: 600,
        flexShrink: 0,
        ...sx,
      }}
    >
      {glyph}
    </Avatar>
  );
};

export default IntegrationLogo;
